import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, Loader2, ShieldCheck, ShieldAlert, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  high: 'bg-red-50 border-red-200 text-red-800',
  moderate: 'bg-amber-50 border-amber-200 text-amber-800',
  low: 'bg-slate-50 border-slate-200 text-slate-700',
};

/**
 * Quick Re-Check modal — one-tap trigger to re-run the drug-interaction
 * analysis against the user's current active med reminders. No full retake.
 *
 * Three phases in one modal:
 *   1. "confirm" — explain what we're about to do + medications preview
 *   2. "loading" — while the AI works
 *   3. "result"  — show interactions found (or all clear)
 */
export default function QuickRecheckModal({
  open,
  onClose,
  onSaved,        // (result) => void  called after a successful recheck
  driftInfo,      // { added_meds: [...], removed_meds: [...] } from /assessment/med-drift
  getAuthHeader,
}) {
  const [phase, setPhase] = useState('confirm');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runRecheck = async () => {
    setPhase('loading');
    setError(null);
    try {
      const res = await axios.post(
        `${API}/assessment/quick-recheck`,
        {},
        { headers: getAuthHeader() }
      );
      setResult(res.data);
      setPhase('result');
      onSaved?.(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Re-check failed — try again in a moment';
      setError(msg);
      setPhase('confirm');
      toast.error(msg);
    }
  };

  const handleClose = () => {
    if (phase === 'loading') return; // block accidental dismiss mid-request
    setPhase('confirm');
    setResult(null);
    setError(null);
    onClose?.();
  };

  const addedNames = (driftInfo?.added_meds || []).map(m => m?.name).filter(Boolean);
  const removedNames = (driftInfo?.removed_meds || []).map(m => m?.name).filter(Boolean);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={handleClose}
          data-testid="quick-recheck-overlay"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="quick-recheck-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-border px-5 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-[#1A1A1A]">Quick Re-Check</h3>
                  <p className="text-xs text-muted-foreground">Interaction analysis · no retake needed</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={phase === 'loading'}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center disabled:opacity-40"
                data-testid="quick-recheck-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5">
              {/* CONFIRM PHASE */}
              {phase === 'confirm' && (
                <div className="space-y-4" data-testid="quick-recheck-confirm">
                  <p className="text-sm text-[#1A1A1A] leading-relaxed">
                    We&apos;ll re-run the drug-interaction analysis using your current medication reminders and your existing supplement stack. Takes about 20 seconds.
                  </p>

                  {addedNames.length > 0 && (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 mb-1.5">
                        New since last check
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {addedNames.map(n => (
                          <span key={n} className="inline-flex items-center px-2 py-0.5 rounded-md bg-white border border-emerald-200 text-xs font-medium text-emerald-800">
                            + {n}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {removedNames.length > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                        Removed since last check
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {removedNames.map(n => (
                          <span key={n} className="inline-flex items-center px-2 py-0.5 rounded-md bg-white border border-slate-200 text-xs font-medium text-slate-700 line-through">
                            {n}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {error && (
                    <p className="text-xs text-red-600 bg-red-50 p-2 rounded-lg">{error}</p>
                  )}

                  <Button
                    onClick={runRecheck}
                    className="w-full"
                    data-testid="quick-recheck-run"
                  >
                    Run interaction check
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
                    Educational only — not a substitute for advice from your provider.
                  </p>
                </div>
              )}

              {/* LOADING PHASE */}
              {phase === 'loading' && (
                <div className="py-12 flex flex-col items-center gap-4" data-testid="quick-recheck-loading">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Analyzing potential interactions…</p>
                </div>
              )}

              {/* RESULT PHASE */}
              {phase === 'result' && result && (
                <div className="space-y-4" data-testid="quick-recheck-result">
                  {result.interaction_count === 0 ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-emerald-900">No interactions flagged</p>
                        <p className="text-xs text-emerald-800/80 mt-1">
                          Your {result.medications.length} medication{result.medications.length !== 1 ? 's' : ''} + supplement stack look clear right now.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <ShieldAlert className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-amber-900">
                            {result.interaction_count} potential interaction{result.interaction_count !== 1 ? 's' : ''} flagged
                          </p>
                          <p className="text-xs text-amber-800/80 mt-1">
                            Review below and discuss with your provider.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2" data-testid="quick-recheck-interactions">
                        {result.drug_interactions.map((it, i) => (
                          <div
                            key={i}
                            className={`rounded-xl border p-3 ${SEVERITY_STYLES[it.severity] || SEVERITY_STYLES.moderate}`}
                            data-testid={`quick-recheck-interaction-${i}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="font-semibold text-xs">
                                {it.medication} ↔ {it.supplement}
                              </p>
                              <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-white/80">
                                {it.severity}
                              </span>
                            </div>
                            {it.description && (
                              <p className="text-xs opacity-90 leading-relaxed">{it.description}</p>
                            )}
                            {it.recommendation && (
                              <p className="text-xs font-medium mt-1.5 opacity-95">
                                💡 {it.recommendation}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <Button
                    onClick={handleClose}
                    variant="outline"
                    className="w-full"
                    data-testid="quick-recheck-done"
                  >
                    Done
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
